'use client';

import { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SearchBarProps {
  onSearch: (query: string) => void;
  onVoiceSearch?: () => void;
}

const SearchBar = ({ onSearch, onVoiceSearch }: SearchBarProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const popularSearches = [
    'Fresh Salmon',
    'Tuna Steaks',
    'Prawns',
    'Mackerel',
    'Cod Fillets',
    'Sea Bass',
    'Sardines',
    'Lobster',
  ];

  useEffect(() => {
    if (searchQuery.length > 0) {
      const filtered = popularSearches.filter((search) =>
        search.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    onSearch(query);
    setShowSuggestions(false);
  };

  const handleVoiceClick = () => {
    setIsVoiceActive(true);
    onVoiceSearch?.();
    setTimeout(() => setIsVoiceActive(false), 3000);
  };

  const handleClear = () => {
    setSearchQuery('');
    onSearch('');
    inputRef.current?.focus();
  };

  return (
    <div className="relative w-full">
      <div className="relative flex items-center">
        <Icon
          name="MagnifyingGlassIcon"
          size={20}
          className="absolute left-4 text-muted-foreground"
        />
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
          placeholder="Search for fish, cuts, or products..."
          className="w-full pl-12 pr-24 py-3 bg-background border border-input rounded-md text-sm focus-ring"
        />
        <div className="absolute right-2 flex items-center gap-1">
          {searchQuery && (
            <button
              onClick={handleClear}
              className="p-2 hover:bg-muted rounded-md transition-smooth"
              aria-label="Clear search"
            >
              <Icon name="XMarkIcon" size={18} className="text-muted-foreground" />
            </button>
          )}
          <button
            onClick={handleVoiceClick}
            className={`p-2 rounded-md transition-smooth ${
              isVoiceActive
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted text-muted-foreground'
            }`}
            aria-label="Voice search"
          >
            <Icon
              name="MicrophoneIcon"
              size={18}
              className={isVoiceActive ? 'animate-pulse-subtle' : ''}
            />
          </button>
          <button
            onClick={() => handleSearch(searchQuery)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift"
          >
            Search
          </button>
        </div>
      </div>

      {/* Search Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-md shadow-elevation-lg animate-slide-down z-50"
        >
          <div className="p-2">
            <div className="px-3 py-2 text-xs font-caption text-muted-foreground uppercase tracking-wide">
              Suggestions
            </div>
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSearch(suggestion)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted transition-smooth text-left"
              >
                <Icon name="MagnifyingGlassIcon" size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground">{suggestion}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Popular Searches */}
      {!searchQuery && (
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="text-xs font-caption text-muted-foreground">Popular:</span>
          {popularSearches.slice(0, 5).map((search, index) => (
            <button
              key={index}
              onClick={() => handleSearch(search)}
              className="px-3 py-1 bg-muted text-foreground rounded-full text-xs font-medium hover:bg-primary hover:text-primary-foreground transition-smooth"
            >
              {search}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;